import {
  always,
  cond,
  equals,
  pathOr,
  T,
  curry,
  lensPath,
  set,
  map,
  head,
  find,
} from 'ramda';
import {
  doNothing,
} from 'src/services/constants';
import {
  mkInsertAPIContext,
  mkUpdateAPIContext,
  updateContextInResource,
} from 'src/features/core/Intents/ApiContext/builder';
import { IAPIContextFields } from './types';
import { isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import {
  API,
  COPY_TO_UPDATE,
  DELETE,
  INSERT,
  UPDATE,
} from 'src/features/constants';
import { resourceFilterConditionExecutor } from 'src/components/displayerConditionFn';
import { isFunction } from 'src/utilities/isFunction';
import { conditionalFuncs } from 'src/utilities/conditionalFn';
import { splitTheSavedDataPathWithDot } from '../../ChatEngine/Configs/autoAnswerConfigurator';
import { compose } from 'recompose';
import { StateStore, getStoreBasedOnAuthenticationType } from '../../ChatEngine/Configs/apiResouces';
import { APIResourceValueEvaluator } from '../../ChatEngine/Configs/apiResourceFields';
import { LENDSMART_SH_FIELD, LENDSMART_SH_VALUE } from '../intentConstants';
import { ServiceData } from 'src/services/serviceBuilder';
import { getApiResourceName } from '../../ChatEngine/Bot/simpleBotServiceUtil';
import { isArray } from 'src/utilities/types';
import { uniqueId } from 'src/utilities/borrowers';


/////////////// LOCAL PRIVATE
export class BaseAPIContextFields implements IAPIContextFields {
  private apiContext: Object;
  private stateStore: StateStore;
  private answer: any;
  storeClass: any;
  constructor(apiContext: Object, stateStore: StateStore, answer?: any) {
    this.apiContext = apiContext;
    this.stateStore = stateStore;
    this.answer = answer;
    this.storeClass = getStoreBasedOnAuthenticationType();

  }

  getApiResource() {
    return this.stateStore;
  }
  getAnswer() {
    return this.answer;
  }

  getMode() {
    return pathOr('', ['mode'], this.apiContext);
  }
  getActivateUpon() {
    return pathOr('', ['conditions'], this.apiContext);
  }
  getContextFields() {
    return this.replaceSpecialKeyFields(pathOr([], ['context_fields'], this.apiContext));
  }

  /**
   * this method will replace special keyfields ex - ___user_type___ will be replaced with current user_type from configured path from src/features/core/ChatEngine/Configs/apiResourceFields.ts
   * **/
  replaceSpecialKeyFields = (contextFields: any) => {
    const store = new this.storeClass();
    const apiResourceClass = new APIResourceValueEvaluator(store);
    return map((context: string) => {
      return compose(
        set(lensPath([LENDSMART_SH_FIELD]), apiResourceClass.replace(pathOr('', [LENDSMART_SH_FIELD], context))),
        set(lensPath([LENDSMART_SH_VALUE]), apiResourceClass.replace(pathOr('', [LENDSMART_SH_VALUE], context)))
      )(context)
    }, contextFields);

  }

  getPathsToupdate() {
    return pathOr('', ['paths_to_update'], this.apiContext);
  }
  getContext() {
    return pathOr('', ['context'], this.apiContext);
  }
  getRequestEntity() {
    return pathOr(API, ['request_entity'], this.apiContext);
  }
  getRollback() {
    return pathOr({}, ['rollback'], this.apiContext);
  }
  getDeactivatedUpon() {
    return pathOr({}, ['deactivated_upon'], this.apiContext);
  }
  getResourceFinder() {
    return pathOr({}, ['resource_finder'], this.apiContext);
  }
}

export interface IAPIContextDispatcher {
  getAPIContext: () => IAPIContextFields;
  perform: () => any;
  rollback: (dispatch: any) => any;
  postPerform: () => any;
}

export abstract class BaseAPIContextDispatcher
  implements IAPIContextDispatcher {
  private act: IAPIContextFields;
  constructor(act: IAPIContextFields) {
    this.act = act;
  }

  perform() { }

  postPerform() { }

  rollback(dispatch: any) {
    const rd = new RollbackManager(this.act, dispatch);
    const { status, data } = rd.canRollback();
    if (status) {
      return rd.doAction(data);
    }
    return
  }

  getAPIContext() {
    return this.act;
  }
}

export class InsertDispatcher extends BaseAPIContextDispatcher {
  ac: IAPIContextFields;
  dispatch: (data: any) => any;
  apiResource?: StateStore;
  constructor(
    ac: IAPIContextFields,
    dispatch: (data: any) => any,
    apiResource?: StateStore
  ) {
    super(ac);
    this.ac = ac;
    this.apiResource = apiResource;
    this.dispatch = dispatch;
  }

  perform() {
    const rd = new RollbackManager(this.ac, this.dispatch);
    if (rd.canPerform()) {
      return this.rollbackPerform();
    }
    return
  }

  rollbackPerform() {
    const apiResouce = getApiResourceName(this.ac.getContext());
    return {
      kind: apiResouce,
      payload: this.mkPayload(),
      isNew: true,
    } as ServiceData
  }

  private mkPayload() {
    const payload = mkInsertAPIContext(
      this.ac.getContextFields(),
      this.ac.getApiResource(),
      this.ac.getContext(),
      this.ac.getAnswer()

    );
    return payload;
  }
}

export class UpdateDispatcher extends BaseAPIContextDispatcher {
  ac: IAPIContextFields;
  dispatch: (data: any) => any;
  apiResource?: StateStore;
  answer?: any;
  constructor(
    ac: IAPIContextFields,
    dispatch: (data: any) => any,
    apiResource?: StateStore
  ) {
    super(ac);
    this.ac = ac;
    this.apiResource = apiResource;
    this.dispatch = dispatch;
  }
  perform() {
    const rd = new RollbackManager(this.ac, this.dispatch);

    if (rd.canPerform()) {
      return this.rollbackPerform();
    }
    return
  }
  rollbackPerform() {
    const apiResouce = getApiResourceName(this.ac.getContext());
    return {
      kind: apiResouce,
      payload: this.mkPayload()
    } as ServiceData
  }

  private mkPayload() {
    const payload = mkUpdateAPIContext(
      this.ac.getContextFields(),
      this.ac.getContext(),
      this.ac.getApiResource(),
      this.ac.getAnswer(),
      this.ac.getResourceFinder(),

    );
    return payload;
  }
}

export class CopyToUpdateDispatcher extends BaseAPIContextDispatcher {
  ac: IAPIContextFields;
  dispatch: (data: any) => any;
  apiResource?: any;
  constructor(
    ac: IAPIContextFields,
    dispatch: (data: any) => any,
    apiResource?: any
  ) {
    super(ac);
    this.ac = ac;
    this.apiResource = apiResource;
    this.dispatch = dispatch;
  }
  perform() {
    const rd = new RollbackManager(this.ac, this.dispatch);
    if (rd.canPerform()) {
      return this.rollbackPerform();
    }
    return
  }

  rollbackPerform() {
    return this.copyToUpdate(this.ac.getApiResource(), this.ac.getPathsToupdate())
  }

  private contextFieldsUpdate(apiResourceData: any, contextFields: any) {
    return updateContextInResource(
      apiResourceData,
      contextFields,
      this.ac.getAnswer());
  }

  /**
   * 
   * This method will return value of resource to be copied from based on resource type
   * 
   */
  getCopyFromUpdateValue = (copyFrom: any, resources: any) => {
    return cond([
      [equals('declaration'), always(this.getDeclarationValueFromPath(copyFrom, resources))],
      [T, always(pathOr(
        '',
        splitTheSavedDataPathWithDot(
          pathOr('', ['path'], copyFrom)
        ),
        pathOr('', [pathOr('', ['resource'], copyFrom)], resources)
      ))],
    ])(pathOr('', ['resource'], copyFrom))
  }

  /**
 * 
 * This method will get the value  of declaration consents from the configured path 
 * 
 * 
 */
  getDeclarationValueFromPath = (copyFrom: any, resources: any) => {
    const resource = pathOr({}, [pathOr('', ['resource'], copyFrom)], resources)
    const path = pathOr('', ['path'], copyFrom)
    return pathOr('', ['value'], find((consent: any) => {
      return equals(path, pathOr('', ['name'], consent))
    }, pathOr([], ['consents'], resource)));
  }

  copyToUpdate = (apiResource: any, copytoUpdates: any) => {
    const resources = apiResource.getStateFromStore()
    const getValues = (copytoUpdate: any) => {
      const copyTo = pathOr("", ["copy_to"], copytoUpdate);
      const copyFrom = pathOr("", ["copy_from"], copytoUpdate);
      const copyToResource = pathOr("", ["resource"], copyTo);
      const payload = compose(
        set(
          lensPath(
            splitTheSavedDataPathWithDot(
              pathOr('', ['path'], copyTo)
            )),
          this.getCopyFromUpdateValue(copyFrom, resources)
        )
      )(
        pathOr({}, [copyToResource], resources)
      );
      const apiResouce = getApiResourceName(copyToResource);
      const contextFields = this.ac.replaceSpecialKeyFields(pathOr([], ['context_fields'], copytoUpdate))
      return {
        kind: apiResouce,
        payload: this.contextFieldsUpdate(
          payload,
          contextFields
        ),
      } as ServiceData
    };
    const toUpdateResources = map(getValues, copytoUpdates);
    return toUpdateResources
  };
}


export class DeleteDispatcher extends BaseAPIContextDispatcher {
  ac: IAPIContextFields;
  dispatch: (data: any) => any;
  apiResource?: any;
  constructor(
    ac: IAPIContextFields,
    dispatch: (data: any) => any,
    apiResource?: any
  ) {
    super(ac);
    this.ac = ac;
    this.apiResource = apiResource;
    this.dispatch = dispatch;
  }

  perform() {
    const rd = new RollbackManager(this.ac, this.dispatch);
    if (rd.canPerform() && isNotEmpty(uniqueId(this.mkPayload()))) {
      return this.rollbackPerform();
    }
    return
  }
  rollbackPerform() {
    const apiResouce = getApiResourceName(this.ac.getContext());
    // we don't support delete API on Batch call so we use individual 
    return returnIsNotEmptyOrTrue(
      uniqueId(this.mkPayload()),
      {
        kind: apiResouce,
        payload: this.mkPayload(),
        isIndividualRequest: true,
        isDelete: true,
      } as ServiceData
    ) 
  }

  private mkPayload() {
    const result = isNotEmpty(this.apiResource)
      ? returnIsNotEmptyOrTrue(
        isArray(this.apiResource),
        head(this.apiResource),
        this.apiResource
      ) : {};
    return result
  }
}


export class APIDispatcher {
  act: IAPIContextFields;
  action: string;
  dispatch: (data: any) => void;
  apiResource?: any;
  constructor(
    act: IAPIContextFields,
    action: string,
    dispatch: (data: any) => void,
    apiResource?: any
  ) {
    this.act = act;
    this.action = action;
    this.dispatch = dispatch;
    this.apiResource = apiResource;
  }

  mkDispatcher() {
    const result = cond([
      [
        equals(INSERT),
        always(new InsertDispatcher(this.act, this.dispatch, this.apiResource)),
      ],
      [
        equals(UPDATE),
        always(new UpdateDispatcher(this.act, this.dispatch, this.apiResource)),
      ],
      [
        equals(COPY_TO_UPDATE),
        always(
          new CopyToUpdateDispatcher(this.act, this.dispatch, this.apiResource)
        ),
      ],
      [
        equals(DELETE),
        always(new DeleteDispatcher(this.act, this.dispatch, this.apiResource)),
      ],
      [T, always(doNothing)],
    ])(this.action);
    return result;
  }
}

interface IRollbackManager {
  doAction: (data: any) => any;
  canRollback: () => any;
}


export class RollbackManager implements IRollbackManager {
  apiContext: IAPIContextFields;
  dispatch: (data: any) => void;
  constructor(apiContext: IAPIContextFields, dispatch: (data: any) => void) {
    this.apiContext = apiContext;
    this.dispatch = dispatch;
  }
  canRollback() {
    const { data, status } = this.getRollbackStatus(
      this.apiContext.getRollback(),
      this.apiContext.getApiResource(),
      this.apiContext.getContext()
    );
    return { status, data };
  }
  canPerform() {
    const status = this.getDeactivatedUponStatus(
      this.apiContext.getDeactivatedUpon(),
      this.apiContext.getApiResource(),
      this.apiContext.getContext()
    );
    return equals(status, false);
  }

  getRollbackStatus = (
    rollBack: any,
    apiResource: any,
    apiResourceName: any
  ) => {
    if (isNotEmpty(rollBack)) {
      const resources = pathOr([], [apiResourceName], apiResource.getStateFromStore());
      const result = resourceFilterConditionExecutor(rollBack, resources);
      return { data: result, status: isNotEmpty(result) };
    } else {
      return { status: false };
    }
  };

  getDeactivatedUponStatus = (
    deactivateUpon: any,
    apiResource: any,
    apiResourceName: any
  ) => {

    if (isNotEmpty(deactivateUpon)) {
      const resources = pathOr([], [apiResourceName], apiResource.getStateFromStore());
      const args = resourceFilterConditionExecutor(deactivateUpon, resources);
      const result = curry(
        conditionalFuncs(pathOr('', ['apply_rule'], deactivateUpon))
      )(...args);
      return isFunction(result) ? false : !!result;
    } else {
      return false;
    }
  };


  doAction(data: any): any {
    const apiDispatcher = new APIDispatcher(
      this.apiContext,
      pathOr('', ['and_then'], this.apiContext.getRollback()),
      this.dispatch,
      data
    );
    const result = apiDispatcher.mkDispatcher();
    return result.rollbackPerform();
  }
}

